import { useCallback, useState } from 'react'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { RecordFactory } from '../../models/RecordFactory'
import { dateFormat } from '../../utils/dates'
import { e164ToPhone } from '../../utils/phone'
import { PSApplication } from '../app/PSApplication'
import Button from '../app/design/Button'
import Modal from '../app/design/Modal'
import { Chip } from '../common/fields/Chip'
import { TextField } from '../common/fields/TextField'
import { loginTTLOptions } from '../settings/Options'
import { WorkflowPage } from '../workflows/WorkflowPage'

type Props = {
    isOpen: boolean
    onClose: () => void
    onBack: () => void
}

export const LoginAndSecurityModal = ({ isOpen, onClose, onBack }: Props) => {
    const { logAndDisplayError } = useErrorHandler()

    const user = PSApplication.me()
    const device = PSApplication.device()

    const [isSaving, setIsSaving] = useState(false)
    const [firstName, setFirstName] = useState(user.firstName ?? '')
    const [lastName, setLastName] = useState(user.lastName ?? '')
    const [password, setPassword] = useState(user.password)
    const [ttl, setTTL] = useState(device.ttl?.toString() ?? loginTTLOptions[0].value)

    const loginDate = device.lastLoginDate
    const phone = e164ToPhone(user.e164)
    const passwordMeetsRequirements = password?.length === 4 || !password?.length // allow only empty password or a 4 digit pin

    const hasEdits =
        user.password !== password ||
        device.ttl?.toString() !== ttl ||
        firstName !== user.firstName ||
        lastName !== user.lastName

    const onSave = useCallback(async () => {
        if (!hasEdits) {
            onBack()
            return
        }

        setIsSaving(true)
        const result = await RecordFactory.getInstance().submitLoginSettings(
            device,
            Number(ttl),
            `${firstName} ${lastName}`,
            password
        )
        setIsSaving(false)

        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        onBack()
    }, [device, password, ttl, onBack, hasEdits, firstName, lastName, logAndDisplayError])

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            onNavigateBack={onBack}
            title="Login, security & user settings"
            footer={
                <Button intent="primary" onClick={onSave} isLoading={isSaving}>
                    Save
                </Button>
            }
        >
            <div className="flex flex-col items-stretch justify-start gap-y-6">
                <WorkflowPage.IconSection title="First name" icon="user.large">
                    <TextField
                        size="medium"
                        value={firstName}
                        placeholder="Enter first name"
                        onChange={(event) => {
                            setFirstName(event.target.value)
                        }}
                        onClear={() => setFirstName('')}
                    />
                </WorkflowPage.IconSection>

                <WorkflowPage.IconSection title="Last name" icon="user.large">
                    <TextField
                        size="medium"
                        value={lastName}
                        placeholder="Enter last name"
                        onChange={(event) => {
                            setLastName(event.target.value)
                        }}
                        onClear={() => setLastName('')}
                    />
                </WorkflowPage.IconSection>

                <WorkflowPage.IconSection
                    title="Login"
                    icon="user.large"
                    disabled
                    description={`Your last login with this device was on ${dateFormat(loginDate, 'ddd[.] MMM Do YYYY')} at ${dateFormat(loginDate, 'h:mm a')}.`}
                >
                    <TextField
                        size="medium"
                        value={phone}
                        disabled={true}
                        className="text-gray-400"
                    />
                </WorkflowPage.IconSection>

                <WorkflowPage.IconSection
                    title="Password"
                    icon="shieldLock.large"
                    description={
                        user?.password?.length
                            ? 'Your login is via password. Delete your password to switch to text message.'
                            : 'Your login is via text message. Enter a 4 digit pin to switch to password.'
                    }
                >
                    <TextField
                        size="medium"
                        type="password"
                        value={password}
                        placeholder="Enter password"
                        onChange={(event) => {
                            if (/^[0-9]{0,4}$/.test(event.target.value)) {
                                setPassword(event.target.value)
                            }
                        }}
                        onKeyDown={(event) => {
                            // delete all XXXX values on backspace so we don't get into a bad place where pw can be e.g. xxx4
                            if (event.key === 'Backspace' && /^xxxx$/.test(password ?? '')) {
                                setPassword('')
                            }
                        }}
                        maxLength={4}
                        hasError={!passwordMeetsRequirements}
                        onClear={() => setPassword('')}
                    />
                </WorkflowPage.IconSection>

                <WorkflowPage.IconSection title="Require login every" icon="refresh.large">
                    <div className="flex flex-wrap gap-x-2 gap-y-2">
                        {loginTTLOptions.map((option) => (
                            <Chip
                                key={option.value}
                                size="small"
                                title={option.label}
                                isSelected={ttl === option.value}
                                onClick={() => setTTL(option.value)}
                            />
                        ))}
                    </div>
                </WorkflowPage.IconSection>
            </div>
        </Modal>
    )
}
